/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import {
  FinancialGateway,
  CustomerData,
  CustomerResponse,
  TransferRequest,
  TransferResponse,
  ExternalPixKeyConsultRequest,
  ExternalPixKeyConsultResponse,
  WebhookProcessResult,
} from "../FinancialGateway";
import { PaymentGateway } from "../PaymentGateway";
import { PaymentRequest, PaymentResponse, PaymentStatusResponse } from "../types/financial.types";
import {
  CreateSubscriptionRequest,
  SubscriptionResponse,
  CancelSubscriptionResponse,
  UpdateSubscriptionRequest,
  ListSubscriptionPaymentsResponse,
  SubscriptionRecord,
  SubscriptionPaymentRecord,
} from "../types/subscription.types";
import { getSystemSettings } from "../../../utils/commissionConfig";
import { generateFallbackPixPayload, generatePixExpirationTimestamps } from "../utils/financialCalculations";
import { PaymentEnvironmentService } from "../PaymentEnvironmentService";
import { isSandboxApiKey, isProductionApiKey } from "../utils/financialValidation";
import { getAuthHeaders } from "../../../utils/firebase";
import { normalizePixKeyType, isValidCpf, isValidCnpj } from "../../../utils/pixValidator";

export class AsaasGateway implements FinancialGateway, PaymentGateway {
  readonly id = "asaas" as const;
  readonly name = "Asaas Gateway (Pix Instantâneo & Mensalidades)";

  static mockPaymentStatuses: Map<string, { isPaid: boolean; status: string; value?: number }> = new Map();

  static setMockPaymentStatus(paymentId: string, isPaid: boolean, status: string = "RECEIVED", value?: number): void {
    this.mockPaymentStatuses.set(paymentId, { isPaid, status, value });
  }

  private getBaseUrl(): string {
    return PaymentEnvironmentService.getBaseUrl();
  }

  private async parseResponseSafely(res: Response, endpointLabel: string) {
    const status = res.status;
    const statusText = res.statusText;
    const url = res.url;
    const text = await res.text().catch(() => "");

    console.log(`[ASAAS API ${endpointLabel}] STATUS: ${status} ${statusText} | URL: ${url}`);
    let json: any = null;
    if (text && text.trim().length > 0) {
      try {
        json = JSON.parse(text);
      } catch (e) {
        console.error(`[ASAAS API ${endpointLabel}] Failed to parse JSON response. Body was:`, text);
      }
    }

    if (!res.ok) {
      if (json?.errors && Array.isArray(json.errors)) {
        json.errors.forEach((errItem: any) => {
          console.error(`[ASAAS REJECTION] code=${errItem.code || "UNKNOWN"} | description=${errItem.description || errItem.message || JSON.stringify(errItem)}`);
        });
      }
      console.error(`[ASAAS RESPONSE ERROR] status=${status} endpoint=${endpointLabel} body=${text}`);
    }

    return { status, statusText, url, text, json, ok: res.ok };
  }

  /**
   * Roteia chamadas Asaas exclusivamente pelo proxy de backend seguro (/api/asaas/proxy),
   * onde as chaves e segredos do Secret Manager são injetados exclusivamente no servidor.
   * NUNCA expõe chaves no frontend e NUNCA faz fallback para chamadas diretas no browser.
   */
  private async fetchAsaasApi(url: string, options: RequestInit = {}): Promise<Response> {
    const headersObj = (options.headers || {}) as Record<string, string>;
    const customToken = headersObj.access_token || headersObj.Access_Token || headersObj.ACCESS_TOKEN;

    const reqHeaders: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (customToken) {
      reqHeaders["access_token"] = customToken;
    } else if (typeof window === "undefined") {
      const activeEnv = PaymentEnvironmentService.getActiveEnvironment();
      const serverToken = PaymentEnvironmentService.getApiKey(activeEnv) || PaymentEnvironmentService.getApiKey();
      if (serverToken) {
        reqHeaders["access_token"] = serverToken;
      }
    }

    const method = (options.method || "GET").toUpperCase();
    const isBodyAllowed = method !== "GET" && method !== "HEAD";

    let reqBody: any = undefined;
    if (isBodyAllowed && options.body !== undefined && options.body !== null) {
      if (typeof options.body === "string") {
        try {
          reqBody = JSON.parse(options.body);
        } catch (e) {
          reqBody = options.body;
        }
      } else {
        reqBody = options.body;
      }
    }

    // No ambiente Node.js / Server-side
    if (typeof window === "undefined") {
      const fetchOpts: RequestInit = {
        method,
        headers: reqHeaders,
      };
      if (isBodyAllowed && options.body !== undefined && options.body !== null) {
        fetchOpts.body = typeof options.body === "string" ? options.body : JSON.stringify(options.body);
      }
      return fetch(url, fetchOpts);
    }

    // No Browser: Encaminha EXCLUSIVAMENTE para o Proxy Seguro do Backend (/api/asaas/proxy)
    // O Proxy é chamado SEMPRE com método POST para transportar com segurança a URL, método e corpo.
    const proxyPayload: Record<string, any> = {
      url,
      method,
      headers: reqHeaders,
      ...((options as any).authorizationExternalReference ? { authorizationExternalReference: (options as any).authorizationExternalReference } : {}),
    };
    if (isBodyAllowed && reqBody !== undefined) {
      proxyPayload.body = reqBody;
    }

    const authHeaders = await getAuthHeaders({
      "Content-Type": "application/json",
    });

    const proxyRes = await fetch("/api/asaas/proxy", {
      method: "POST", // SEMPRE POST ao proxy interno para enviar o envelope
      headers: authHeaders,
      body: JSON.stringify(proxyPayload),
    });

    const contentType = proxyRes.headers.get("content-type") || "";
    const cloned = proxyRes.clone();
    const peekText = (await cloned.text().catch(() => "")).trim();

    const isHtmlFallback =
      contentType.includes("text/html") ||
      peekText.startsWith("<!doctype") ||
      peekText.startsWith("<html");

    if (isHtmlFallback) {
      throw new TypeError("O backend proxy /api/asaas/proxy não respondeu com JSON.");
    }

    return proxyRes;
  }

  private isSimulationOrTest(): boolean {
    if ((this as any).fetchAsaasApi !== AsaasGateway.prototype.fetchAsaasApi) {
      return false;
    }
    if (typeof globalThis.fetch === "function" && Boolean((globalThis.fetch as any).mock)) {
      return false;
    }
    const settings = getSystemSettings();
    return settings.paymentEnvironmentMode === "simulation" || process.env.NODE_ENV === "test" || Boolean(process.env.VITEST);
  }

  // ==========================================
  // --- CLIENTES / CUSTOMERS ---
  // ==========================================

  async createCustomer(data: CustomerData): Promise<CustomerResponse> {
    if (!data.name || data.name.trim().length < 2 || !data.email || !data.email.includes("@")) {
      return {
        success: false,
        failedStep: "CUSTOMER_VALIDATION",
        errorMessage: "Dados cadastrais incompletos para criação de cliente no Asaas: nome e email válidos são obrigatórios.",
      };
    }

    if (this.isSimulationOrTest()) {
      const simId = `cust_sim_${Date.now()}`;
      return {
        success: true,
        customerId: simId,
        customer: { id: simId, name: data.name, email: data.email, cpfCnpj: data.cpfCnpj },
      };
    }

    const baseUrl = this.getBaseUrl();
    const cleanCpf = data.cpfCnpj ? data.cpfCnpj.replace(/\D/g, "") : "";
    const cleanEmail = data.email.trim();
    const cleanName = data.name.trim();

    try {
      const customerBody: any = {
        name: cleanName,
        email: cleanEmail,
        // Bloqueia cobrança de taxa de mensageria Asaas: notificações são de responsabilidade do Hub Esportivo
        notificationDisabled: data.notificationDisabled !== undefined ? data.notificationDisabled : true,
      };
      const isCpfCnpjValid = cleanCpf && (isValidCpf(cleanCpf) || isValidCnpj(cleanCpf));
      if (isCpfCnpjValid) {
        customerBody.cpfCnpj = cleanCpf;
      }
      if (data.phone) {
        customerBody.mobilePhone = data.phone.replace(/\D/g, "");
      }
      if (data.postalCode) {
        customerBody.postalCode = data.postalCode.replace(/\D/g, "");
      }
      if (data.address) {
        customerBody.address = data.address;
        customerBody.addressNumber = data.addressNumber || "S/N";
      }
      if (data.externalReference) {
        customerBody.externalReference = data.externalReference;
      }

      const createRes = await this.fetchAsaasApi(`${baseUrl}/customers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(customerBody),
      });

      const createParsed = await this.parseResponseSafely(createRes, "POST /customers");
      if (createParsed.ok && createParsed.json?.id) {
        return {
          success: true,
          customerId: createParsed.json.id,
          customer: createParsed.json,
        };
      }

      const errorMsg = createParsed.json?.errors?.[0]?.description || `Falha ao criar cliente Asaas (${createParsed.status})`;
      return { success: false, errorMessage: errorMsg };
    } catch (err: any) {
      return { success: false, errorMessage: err.message || String(err) };
    }
  }

  async updateCustomer(customerId: string, data: Partial<CustomerData>): Promise<CustomerResponse> {
    if (this.isSimulationOrTest()) {
      return {
        success: true,
        customerId,
        customer: { id: customerId, ...data },
      };
    }

    const baseUrl = this.getBaseUrl();
    try {
      const updateBody: any = {
        // Garante que notificações pagas do Asaas fiquem desabilitadas
        notificationDisabled: data.notificationDisabled !== undefined ? data.notificationDisabled : true,
      };
      if (data.name) updateBody.name = data.name.trim();
      if (data.email) updateBody.email = data.email.trim();
      if (data.cpfCnpj) updateBody.cpfCnpj = data.cpfCnpj.replace(/\D/g, "");
      if (data.phone) updateBody.mobilePhone = data.phone.replace(/\D/g, "");
      if (data.postalCode) updateBody.postalCode = data.postalCode.replace(/\D/g, "");
      if (data.address) updateBody.address = data.address;
      if (data.addressNumber) updateBody.addressNumber = data.addressNumber;
      if (data.externalReference) updateBody.externalReference = data.externalReference;

      const updateRes = await this.fetchAsaasApi(`${baseUrl}/customers/${customerId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updateBody),
      });

      const updateParsed = await this.parseResponseSafely(updateRes, `POST /customers/${customerId}`);
      if (updateParsed.ok && updateParsed.json?.id) {
        return {
          success: true,
          customerId: updateParsed.json.id,
          customer: updateParsed.json,
        };
      }

      const errorMsg = updateParsed.json?.errors?.[0]?.description || `Falha ao atualizar cliente Asaas (${updateParsed.status})`;
      return { success: false, errorMessage: errorMsg };
    } catch (err: any) {
      return { success: false, errorMessage: err.message || String(err) };
    }
  }

  async getCustomer(customerId: string): Promise<CustomerResponse> {
    if (this.isSimulationOrTest()) {
      return {
        success: true,
        customerId,
        customer: { id: customerId, name: "Customer Sim", email: "sim@test.com" },
      };
    }

    const baseUrl = this.getBaseUrl();
    try {
      const res = await this.fetchAsaasApi(`${baseUrl}/customers/${customerId}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      const parsed = await this.parseResponseSafely(res, `GET /customers/${customerId}`);
      if (parsed.ok && parsed.json?.id) {
        return { success: true, customerId: parsed.json.id, customer: parsed.json };
      }
      return { success: false, errorMessage: `Cliente não encontrado (${parsed.status})` };
    } catch (err: any) {
      return { success: false, errorMessage: err.message || String(err) };
    }
  }

  private async getOrCreateCustomer(
    baseUrl: string,
    name: string,
    email: string,
    cpfCnpj: string
  ): Promise<string | null> {
    const cleanCpf = cpfCnpj ? cpfCnpj.replace(/\D/g, "") : "";
    const cleanEmail = email && email.includes("@") ? email.trim() : "";
    const cleanName = name && name.trim().length >= 2 ? name.trim() : "";

    if (!cleanName || !cleanEmail) {
      return null;
    }

    if (this.isSimulationOrTest()) {
      return `cust_sim_${Date.now()}`;
    }

    try {
      // 1. Busca por CPF/CNPJ se disponível e válido
      if (cleanCpf && (isValidCpf(cleanCpf) || isValidCnpj(cleanCpf))) {
        const searchRes = await this.fetchAsaasApi(`${baseUrl}/customers?cpfCnpj=${cleanCpf}`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        const searchParsed = await this.parseResponseSafely(searchRes, "GET /customers?cpfCnpj");
        if (searchParsed.ok && searchParsed.json?.data && searchParsed.json.data.length > 0) {
          const existingCust = searchParsed.json.data[0];
          if (existingCust.id && existingCust.notificationDisabled !== true) {
            this.updateCustomer(existingCust.id, { notificationDisabled: true }).catch(() => {});
          }
          return existingCust.id;
        }
      }

      // 2. Busca por E-mail se disponível
      if (cleanEmail) {
        const emailRes = await this.fetchAsaasApi(`${baseUrl}/customers?email=${encodeURIComponent(cleanEmail)}`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
        const emailParsed = await this.parseResponseSafely(emailRes, "GET /customers?email");
        if (emailParsed.ok && emailParsed.json?.data && emailParsed.json.data.length > 0) {
          const existingCust = emailParsed.json.data[0];
          const existingCpf = existingCust.cpfCnpj ? existingCust.cpfCnpj.replace(/\D/g, "") : "";
          const isCleanCpfValid = Boolean(cleanCpf && (isValidCpf(cleanCpf) || isValidCnpj(cleanCpf)));
          const needsCpfUpdate = isCleanCpfValid && (!existingCpf || existingCpf.length < 11);
          const needsNotificationUpdate = existingCust.notificationDisabled !== true;

          if (needsCpfUpdate || needsNotificationUpdate) {
            const updatePayload: Partial<CustomerData> = {};
            if (needsNotificationUpdate) updatePayload.notificationDisabled = true;
            if (needsCpfUpdate) updatePayload.cpfCnpj = cleanCpf;
            await this.updateCustomer(existingCust.id, updatePayload).catch((e) => {
              console.warn(`[AsaasGateway] Aviso ao atualizar dados do customer ${existingCust.id}:`, e);
            });
          }
          return existingCust.id;
        }
      }

      // 3. Cria novo cliente com dados reais
      const custRes = await this.createCustomer({
        name: cleanName,
        email: cleanEmail,
        cpfCnpj: cleanCpf || undefined,
        notificationDisabled: true,
      });

      if (custRes.success && custRes.customerId) {
        return custRes.customerId;
      }
    } catch (err) {
      console.error("Asaas customer query/creation exception:", err);
    }

    return null;
  }

  // ==========================================
  // --- COBRANÇAS / PAYMENTS ---
  // ==========================================

  async createPayment(params: PaymentRequest): Promise<PaymentResponse> {
    const settings = getSystemSettings();

    const rawAmount = params.amount !== undefined && params.amount !== null
      ? Number(params.amount)
      : ((params as any).value !== undefined && (params as any).value !== null ? Number((params as any).value) : NaN);
    if (isNaN(rawAmount) || rawAmount <= 0) {
      throw new Error("O valor da cobrança (amount) é obrigatório, deve ser um número válido e maior que zero.");
    }
    let safeAmount = Number(rawAmount.toFixed(2));

    const bookingIdRef = (params.bookingId || params.externalReference || "").trim();
    if (!bookingIdRef) {
      throw new Error("Identificador financeiro de reserva (bookingId / externalReference) é obrigatório para emissão de cobrança no Asaas. Criação de identificadores órfãos foi bloqueada com segurança.");
    }

    const { createdAt, expiresAt } = generatePixExpirationTimestamps();

    // Modo de fallback / simulação local offline: quando em simulação ou ambiente de teste
    if (this.isSimulationOrTest()) {
      const fallback = generateFallbackPixPayload(
        params.arenaPixKey || settings.masterPixKey || "00000000000",
        safeAmount,
        "ARENA ESPORTIVA"
      );

      return {
        success: true,
        paymentId: `ASAAS_SIM_${Date.now()}`,
        qrCode: fallback.qrCode,
        qrCodeBase64: fallback.qrCodeBase64,
        status: "PENDING",
        gateway: "pix_direto",
        isRealGateway: false,
        createdAt,
        expiresAt,
      };
    }

    const baseUrl = this.getBaseUrl();

    try {
      const payerName = params.payerName || params.customerName || "Atleta Hub";
      const payerEmail = params.payerEmail || params.customerEmail || "cliente@hubesportivo.com.br";
      const payerCpf = params.payerCpf || params.customerCpfCnpj || "";
      const cleanPayerCpf = payerCpf ? payerCpf.replace(/\D/g, "") : "";

      let customerId: string | null = params.customerId || null;

      if (customerId) {
        if (cleanPayerCpf && (isValidCpf(cleanPayerCpf) || isValidCnpj(cleanPayerCpf))) {
          try {
            const custCheck = await this.getCustomer(customerId);
            if (custCheck.success && custCheck.customer) {
              const existingCpf = custCheck.customer.cpfCnpj ? custCheck.customer.cpfCnpj.replace(/\D/g, "") : "";
              if (!existingCpf || existingCpf.length < 11) {
                await this.updateCustomer(customerId, { cpfCnpj: cleanPayerCpf });
              }
            }
          } catch (custErr) {
            console.warn(`[AsaasGateway] Aviso ao verificar/atualizar customer ${customerId}:`, custErr);
          }
        }
      } else {
        customerId = await this.getOrCreateCustomer(
          baseUrl,
          payerName,
          payerEmail,
          cleanPayerCpf
        );
      }

      if (!customerId) {
        throw new Error("Falha ao resolver ou cadastrar o cliente (customer) no Asaas.");
      }

      // Vencimento Pix: Hoje
      const today = new Date().toISOString().split("T")[0];

      const paymentBody: any = {
        customer: customerId,
        billingType: "PIX",
        value: safeAmount,
        dueDate: today,
        description: (params.description || "Pagamento Hub Esportivo").substring(0, 500),
        externalReference: bookingIdRef,
      };

      const payRes = await this.fetchAsaasApi(`${baseUrl}/payments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(paymentBody),
      });

      const payParsed = await this.parseResponseSafely(payRes, "POST /payments (PIX)");

      if (!payParsed.ok || !payParsed.json || !payParsed.json.id) {
        let errorMsg = "";
        let errorDetails: any[] = [];
        if (payParsed.json?.errors && Array.isArray(payParsed.json.errors)) {
          errorDetails = payParsed.json.errors;
          const descs = payParsed.json.errors
            .map((e: any) => {
              const code = e.code ? `[${e.code}] ` : "";
              const field = e.field ? `(campo: ${e.field}) ` : "";
              return `${code}${field}${e.description || e.message || JSON.stringify(e)}`;
            })
            .filter(Boolean)
            .join("; ");
          errorMsg = `Asaas: ${descs}`;
        } else {
          errorMsg = payParsed.json?.message || `Erro ao criar cobrança Asaas (${payParsed.status}: ${payParsed.statusText || "Falha"})`;
        }

        const structuredDiagnostics = {
          httpStatus: payParsed.status,
          httpStatusText: payParsed.statusText,
          errors: errorDetails,
          message: errorMsg,
          customerId,
          bookingId: bookingIdRef,
          arenaId: params.arenaId || "N/A",
          financialModel: (params as any).financialModel || (params as any).appliedModel || "SPLIT",
          sanitizedPayload: {
            customer: paymentBody.customer,
            billingType: paymentBody.billingType,
            value: paymentBody.value,
            dueDate: paymentBody.dueDate,
            externalReference: paymentBody.externalReference,
            description: paymentBody.description,
          }
        };

        console.error(`[ASAAS PAYMENT CREATION FAILED]`, JSON.stringify(structuredDiagnostics, null, 2));
        const asaasErr: any = new Error(errorMsg);
        asaasErr.status = payParsed.status;
        asaasErr.diagnostics = structuredDiagnostics;
        throw asaasErr;
      }

      const payData = payParsed.json;
      const extRef = params.externalReference;
      const authExtRef = extRef && extRef.startsWith("plan_change_") ? extRef : undefined;

      // Busca dados do QR Code Pix
      const qrRes = await this.fetchAsaasApi(`${baseUrl}/payments/${payData.id}/pixQrCode`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        ...(authExtRef ? { authorizationExternalReference: authExtRef } : {}),
      } as any);

      const qrParsed = await this.parseResponseSafely(qrRes, "GET /payments/{id}/pixQrCode");

      let qrCode = "";
      let qrCodeBase64 = "";

      if (qrParsed.ok && qrParsed.json) {
        const qrData = qrParsed.json;
        qrCode = qrData.payload || "";
        if (qrData.encodedImage) {
          const cleanImg = qrData.encodedImage.replace(/[\r\n\s]/g, "");
          qrCodeBase64 = cleanImg.startsWith("data:image")
            ? cleanImg
            : `data:image/png;base64,${cleanImg}`;
        }
      }

      if (!qrCodeBase64 && qrCode) {
        qrCodeBase64 = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrCode)}&bgcolor=18181b&color=34d399`;
      }

      return {
        success: true,
        paymentId: payData.id,
        qrCode,
        qrCodeBase64,
        status: payData.status,
        invoiceUrl: payData.invoiceUrl,
        gateway: "asaas",
        isRealGateway: true,
        netValue: payData.netValue !== undefined ? Number(payData.netValue) : undefined,
        feeValue: payData.value && payData.netValue !== undefined ? Number(payData.value) - Number(payData.netValue) : undefined,
        createdAt,
        expiresAt,
      };
    } catch (err: any) {
      console.error("Asaas payment creation exception:", err);

      if (err.diagnostics || err.status || (typeof err.message === "string" && err.message.startsWith("Asaas:"))) {
        return {
          success: false,
          status: "FAILED",
          gateway: "asaas",
          isRealGateway: true,
          errorMessage: err.message || "Cobrança rejeitada pelo Asaas",
          error: err.message,
          diagnostics: err.diagnostics,
          httpStatus: err.status || err.diagnostics?.httpStatus || 400,
          createdAt,
          expiresAt,
        };
      }

      // Se for ambiente de simulação ou teste explícito, permite o fallback local de simulação
      if (this.isSimulationOrTest()) {
        const fallback = generateFallbackPixPayload(
          params.arenaPixKey || settings.masterPixKey,
          params.amount,
          "ARENA ESPORTIVA"
        );

        return {
          success: true,
          paymentId: `ASAAS_SIM_${Date.now()}`,
          qrCode: fallback.qrCode,
          qrCodeBase64: fallback.qrCodeBase64,
          status: "PENDING",
          gateway: "pix_direto",
          isRealGateway: false,
          errorMessage: `Erro de conexão em modo simulação: ${err.message || "Falha"}`,
          createdAt,
          expiresAt,
        };
      }

      // Em ambiente operacional real (sandbox ou produção com tentativa real de comunicação):
      // NUNCA mascarar erro de conexão/proxy como success: true com ASAAS_LOCAL_* e pix_direto
      return {
        success: false,
        status: "FAILED",
        gateway: "asaas",
        isRealGateway: true,
        errorMessage: `Erro de comunicação com o gateway Asaas: ${err?.message || "Falha de conexão"}`,
        error: err?.message || "Falha de conexão",
        diagnostics: err?.diagnostics || null,
        httpStatus: err?.status || err?.diagnostics?.httpStatus || 502,
        createdAt,
        expiresAt,
      };
    }
  }

  async checkPaymentStatus(paymentId: string): Promise<PaymentStatusResponse> {
    if (AsaasGateway.mockPaymentStatuses.has(paymentId)) {
      return AsaasGateway.mockPaymentStatuses.get(paymentId)!;
    }

    if (!paymentId || paymentId.startsWith("ASAAS_SIM_") || paymentId.startsWith("PIX_") || paymentId.startsWith("ASAAS_FALLBACK_") || paymentId.startsWith("ASAAS_LOCAL_")) {
      return { isPaid: false, status: "PENDING" };
    }

    // 1. Prefer calling backend endpoint when running in browser
    if (typeof window !== "undefined") {
      try {
        const authHeaders = await getAuthHeaders({ "Content-Type": "application/json" });
        const serverRes = await fetch(`/api/asaas/check-payment/${paymentId}`, {
          method: "POST",
          headers: authHeaders,
        });
        if (serverRes.ok) {
          const serverData = await serverRes.json();
          const resolvedStatus = serverData.status ?? serverData.paymentStatus ?? "UNKNOWN";
          return {
            isPaid: Boolean(serverData.isPaid),
            status: resolvedStatus
          };
        }
      } catch (e) {
        console.warn("Aviso ao checar pagamento via backend /api/asaas/check-payment, tentando consulta direta Asaas API:", e);
      }
    }

    // 2. Fallback via backend proxy
    const baseUrl = this.getBaseUrl();

    try {
      const res = await this.fetchAsaasApi(`${baseUrl}/payments/${paymentId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const parsed = await this.parseResponseSafely(res, `GET /payments/${paymentId}`);

      if (parsed.ok && parsed.json) {
        const status = parsed.json.status || "";
        const isPaid = status === "RECEIVED" || status === "CONFIRMED" || status === "RECEIVED_IN_CASH";
        return { isPaid, status, value: parsed.json.value };
      }
    } catch (err: any) {
      console.error("Error checking Asaas payment status:", err);
      return { isPaid: false, status: "ERROR", error: err.message };
    }

    return { isPaid: false, status: "UNKNOWN" };
  }

  async cancelPayment(paymentId: string): Promise<{ success: boolean; message?: string; error?: string }> {
    const baseUrl = this.getBaseUrl();
    try {
      const res = await this.fetchAsaasApi(`${baseUrl}/payments/${paymentId}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });
      const parsed = await this.parseResponseSafely(res, `DELETE /payments/${paymentId}`);
      if (parsed.ok && parsed.json?.deleted) {
        return { success: true, message: `Cobrança ${paymentId} cancelada com sucesso.` };
      }
      return { success: false, error: parsed.json?.errors?.[0]?.description || `Falha ao cancelar cobrança (${parsed.status})` };
    } catch (err: any) {
      return { success: false, error: err.message || String(err) };
    }
  }

  async refundPayment(paymentId: string, amount?: number): Promise<{ success: boolean; refundId?: string; error?: string }> {
    const baseUrl = this.getBaseUrl();
    try {
      const res = await this.fetchAsaasApi(`${baseUrl}/payments/${paymentId}/refund`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(amount ? { value: amount } : {}),
      });
      const parsed = await this.parseResponseSafely(res, `POST /payments/${paymentId}/refund`);
      if (parsed.ok && parsed.json) {
        return { success: true, refundId: parsed.json.id || `ref_${Date.now()}` };
      }
      return { success: false, error: parsed.json?.errors?.[0]?.description || `Falha no estorno (${parsed.status})` };
    } catch (err: any) {
      return { success: false, error: err.message || String(err) };
    }
  }

  // ==========================================
  // --- ASSINATURAS E MENSALIDADES / SUBSCRIPTIONS ---
  // ==========================================

  async createSubscription(request: CreateSubscriptionRequest): Promise<SubscriptionResponse> {
    if (this.isSimulationOrTest()) {
      const subId = request.id || `sub_sim_${Date.now()}`;
      return {
        success: true,
        subscriptionId: subId,
        gatewaySubscriptionId: subId,
        status: "ACTIVE",
        cycle: request.cycle || "MONTHLY",
        value: Number(request.value),
        nextDueDate: request.nextDueDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
        gateway: "asaas",
        createdAt: new Date().toISOString(),
      };
    }

    const baseUrl = this.getBaseUrl();

    if (!request.value || Number(request.value) <= 0) {
      throw new Error("O valor da assinatura (value) é obrigatório e deve ser maior que zero.");
    }

    try {
      let customerId = request.customerId;
      if (!customerId) {
        customerId = (await this.getOrCreateCustomer(
          baseUrl,
          request.customerName,
          request.customerEmail,
          request.customerCpfCnpj || ""
        )) || undefined;
      }

      if (!customerId) {
        throw new Error("Falha ao resolver ou cadastrar o cliente (customer) para a assinatura.");
      }

      const nextDueDate = request.nextDueDate || new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split("T")[0];

      const subscriptionBody: any = {
        customer: customerId,
        billingType: request.billingType || "PIX",
        value: Number(Number(request.value).toFixed(2)),
        nextDueDate,
        cycle: request.cycle || "MONTHLY",
        description: (request.description || `Mensalidade - ${request.planName || "AtletaHub"}`).substring(0, 500),
        externalReference: request.externalReference || `sub_${request.userId}_${Date.now()}`,
      };

      if (request.maxPayments) {
        subscriptionBody.maxPayments = request.maxPayments;
      }
      if (request.split && request.split.length > 0) {
        subscriptionBody.split = request.split;
      }

      const res = await this.fetchAsaasApi(`${baseUrl}/subscriptions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(subscriptionBody),
      });

      const parsed = await this.parseResponseSafely(res, "POST /subscriptions");

      if (!parsed.ok || !parsed.json || !parsed.json.id) {
        const errorMsg = parsed.json?.errors?.[0]?.description || `Erro ao criar assinatura Asaas (${parsed.status})`;
        throw new Error(errorMsg);
      }

      const subData = parsed.json;
      return {
        success: true,
        subscriptionId: subData.id,
        gatewaySubscriptionId: subData.id,
        status: (subData.status || "ACTIVE").toUpperCase() as any,
        cycle: (subData.cycle || request.cycle) as any,
        value: subData.value || request.value,
        nextDueDate: subData.nextDueDate,
        gateway: "asaas",
        createdAt: new Date().toISOString(),
      };
    } catch (err: any) {
      console.error("Asaas subscription creation exception:", err);
      return {
        success: false,
        gateway: "asaas",
        errorMessage: err.message || String(err),
      };
    }
  }

  async getSubscription(subscriptionId: string): Promise<{ success: boolean; subscription?: SubscriptionRecord; raw?: any; errorMessage?: string }> {
    if (this.isSimulationOrTest()) {
      const record: SubscriptionRecord = {
        id: subscriptionId,
        userId: "simulation_user",
        gateway: "asaas",
        gatewaySubscriptionId: subscriptionId,
        planId: "plan_fixed_1_2",
        value: 199,
        cycle: "MONTHLY",
        status: "ACTIVE",
        startDate: new Date().toISOString(),
        nextDueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
        cancellationDate: null,
        billingType: "PIX",
        description: "Simulated Subscription",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      return { success: true, subscription: record };
    }

    const baseUrl = this.getBaseUrl();
    try {
      const res = await this.fetchAsaasApi(`${baseUrl}/subscriptions/${subscriptionId}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      const parsed = await this.parseResponseSafely(res, `GET /subscriptions/${subscriptionId}`);

      if (parsed.ok && parsed.json && parsed.json.id) {
        const d = parsed.json;
        const record: SubscriptionRecord = {
          id: d.id,
          userId: d.externalReference?.replace(/^sub_/, "").split("_")[0] || "",
          gateway: "asaas",
          gatewaySubscriptionId: d.id,
          planId: d.externalReference || "",
          value: Number(d.value || 0),
          cycle: d.cycle || "MONTHLY",
          status: (d.status || "ACTIVE").toUpperCase() as any,
          startDate: d.dateCreated || new Date().toISOString(),
          nextDueDate: d.nextDueDate || "",
          cancellationDate: d.cancellationDate || null,
          billingType: d.billingType,
          description: d.description,
          createdAt: d.dateCreated || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        return { success: true, subscription: record, raw: d };
      }

      return { success: false, errorMessage: `Assinatura ${subscriptionId} não encontrada (${parsed.status})` };
    } catch (err: any) {
      return { success: false, errorMessage: err.message || String(err) };
    }
  }

  async cancelSubscription(subscriptionId: string, reason?: string): Promise<CancelSubscriptionResponse> {
    if (this.isSimulationOrTest()) {
      return {
        success: true,
        subscriptionId,
        gatewaySubscriptionId: subscriptionId,
        status: "CANCELLED",
        cancellationDate: new Date().toISOString(),
        message: `Assinatura ${subscriptionId} cancelada com sucesso.`,
      };
    }

    const baseUrl = this.getBaseUrl();
    try {
      const res = await this.fetchAsaasApi(`${baseUrl}/subscriptions/${subscriptionId}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });
      const parsed = await this.parseResponseSafely(res, `DELETE /subscriptions/${subscriptionId}`);

      if (parsed.ok && (parsed.json?.deleted || parsed.json?.id)) {
        return {
          success: true,
          subscriptionId,
          gatewaySubscriptionId: subscriptionId,
          status: "CANCELLED",
          cancellationDate: new Date().toISOString(),
          message: `Assinatura ${subscriptionId} cancelada com sucesso.`,
        };
      }

      const errorMsg = parsed.json?.errors?.[0]?.description || `Falha ao cancelar assinatura (${parsed.status})`;
      return {
        success: false,
        subscriptionId,
        status: "ACTIVE",
        cancellationDate: "",
        errorMessage: errorMsg,
      };
    } catch (err: any) {
      return {
        success: false,
        subscriptionId,
        status: "ACTIVE",
        cancellationDate: "",
        errorMessage: err.message || String(err),
      };
    }
  }

  async updateSubscription(request: UpdateSubscriptionRequest): Promise<SubscriptionResponse> {
    const subId = request.gatewaySubscriptionId || request.subscriptionId;
    if (this.isSimulationOrTest()) {
      return {
        success: true,
        subscriptionId: subId,
        gatewaySubscriptionId: subId,
        status: request.status || "ACTIVE",
        cycle: request.cycle || "MONTHLY",
        value: request.value,
        nextDueDate: request.nextDueDate,
        gateway: "asaas",
        createdAt: new Date().toISOString(),
      };
    }

    const baseUrl = this.getBaseUrl();

    try {
      const updateBody: any = {};
      if (request.value !== undefined) updateBody.value = Number(Number(request.value).toFixed(2));
      if (request.nextDueDate) updateBody.nextDueDate = request.nextDueDate;
      if (request.cycle) updateBody.cycle = request.cycle;
      if (request.billingType) updateBody.billingType = request.billingType;
      if (request.updatePendingPayments !== undefined) updateBody.updatePendingPayments = request.updatePendingPayments;

      const res = await this.fetchAsaasApi(`${baseUrl}/subscriptions/${subId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updateBody),
      });

      const parsed = await this.parseResponseSafely(res, `PUT /subscriptions/${subId}`);

      if (parsed.ok && parsed.json?.id) {
        return {
          success: true,
          subscriptionId: parsed.json.id,
          gatewaySubscriptionId: parsed.json.id,
          status: (parsed.json.status || "ACTIVE").toUpperCase() as any,
          cycle: parsed.json.cycle,
          value: parsed.json.value,
          nextDueDate: parsed.json.nextDueDate,
          gateway: "asaas",
          createdAt: parsed.json.dateCreated,
        };
      }

      return {
        success: false,
        gateway: "asaas",
        errorMessage: parsed.json?.errors?.[0]?.description || `Falha ao atualizar assinatura (${parsed.status})`,
      };
    } catch (err: any) {
      return {
        success: false,
        gateway: "asaas",
        errorMessage: err.message || String(err),
      };
    }
  }

  async listSubscriptionPayments(subscriptionId: string): Promise<ListSubscriptionPaymentsResponse> {
    const baseUrl = this.getBaseUrl();
    try {
      const res = await this.fetchAsaasApi(`${baseUrl}/subscriptions/${subscriptionId}/payments`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      const parsed = await this.parseResponseSafely(res, `GET /subscriptions/${subscriptionId}/payments`);

      if (parsed.ok && parsed.json && Array.isArray(parsed.json.data)) {
        const payments: SubscriptionPaymentRecord[] = parsed.json.data.map((p: any) => ({
          id: p.id,
          subscriptionId,
          gateway: "asaas",
          gatewayPaymentId: p.id,
          gatewaySubscriptionId: subscriptionId,
          value: Number(p.value || 0),
          dueDate: p.dueDate,
          paymentDate: p.paymentDate || p.clientPaymentDate || null,
          status: (p.status || "PENDING").toUpperCase(),
          billingType: p.billingType,
          invoiceUrl: p.invoiceUrl,
          bankSlipUrl: p.bankSlipUrl,
          description: p.description,
          createdAt: p.dateCreated || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }));

        return {
          success: true,
          subscriptionId,
          payments,
          totalCount: payments.length,
        };
      }

      return {
        success: false,
        subscriptionId,
        payments: [],
        totalCount: 0,
        errorMessage: `Nenhum pagamento retornado (${parsed.status})`,
      };
    } catch (err: any) {
      return {
        success: false,
        subscriptionId,
        payments: [],
        totalCount: 0,
        errorMessage: err.message || String(err),
      };
    }
  }

  // ==========================================
  // --- REPASSES E TRANSFERÊNCIAS / TRANSFERS ---
  // ==========================================

  async createTransfer(request: TransferRequest): Promise<TransferResponse> {
    const baseUrl = this.getBaseUrl();
    try {
      const transferBody = {
        value: Number(Number(request.value).toFixed(2)),
        pixAddressKey: request.pixAddressKey,
        pixAddressKeyType: request.pixAddressKeyType,
        description: request.description || "Repasse AtletaHub",
        externalReference: request.externalReference,
        operationType: "PIX",
      };

      const res = await this.fetchAsaasApi(`${baseUrl}/transfers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(transferBody),
      });

      const parsed = await this.parseResponseSafely(res, "POST /transfers");

      if (parsed.ok && parsed.json?.id) {
        return {
          success: true,
          transferId: parsed.json.id,
          status: parsed.json.status || "PENDING",
          effectiveDate: parsed.json.effectiveDate,
          endToEndIdentifier: parsed.json.endToEndIdentifier,
          proofUrl: parsed.json.transactionReceiptUrl,
        };
      }

      return {
        success: false,
        errorMessage: parsed.json?.errors?.[0]?.description || `Falha na transferência (${parsed.status})`,
      };
    } catch (err: any) {
      return { success: false, errorMessage: err.message || String(err) };
    }
  }

  async getTransfer(transferId: string): Promise<TransferResponse> {
    const baseUrl = this.getBaseUrl();
    try {
      const res = await this.fetchAsaasApi(`${baseUrl}/transfers/${transferId}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      const parsed = await this.parseResponseSafely(res, `GET /transfers/${transferId}`);

      if (parsed.ok && parsed.json?.id) {
        return {
          success: true,
          transferId: parsed.json.id,
          status: parsed.json.status,
          effectiveDate: parsed.json.effectiveDate,
          endToEndIdentifier: parsed.json.endToEndIdentifier,
          proofUrl: parsed.json.transactionReceiptUrl,
        };
      }

      return { success: false, errorMessage: `Transferência ${transferId} não encontrada` };
    } catch (err: any) {
      return { success: false, errorMessage: err.message || String(err) };
    }
  }

  // ==========================================
  // --- CONSULTA DE CHAVE PIX EXTERNA / ASPIX ---
  // ==========================================

  async consultExternalPixKey(request: ExternalPixKeyConsultRequest): Promise<ExternalPixKeyConsultResponse> {
    const baseUrl = this.getBaseUrl();
    const type = normalizePixKeyType(request.type);

    let queryKey = request.key ? request.key.trim() : "";
    if (type === "CPF" || type === "CNPJ") {
      queryKey = queryKey.replace(/\D/g, "");
    } else if (type === "PHONE") {
      // No Asaas (GET /v3/pix/addressKeys/external): 11 dígitos incluindo DDD (apenas números)
      let digits = queryKey.replace(/[\s\-\(\)\.]/g, "");
      if (digits.startsWith("+55")) digits = digits.substring(3);
      else if (digits.startsWith("+")) digits = digits.substring(1);
      else if (digits.startsWith("55") && (digits.length === 12 || digits.length === 13)) digits = digits.substring(2);
      queryKey = digits.replace(/\D/g, "");
    } else if (type === "EMAIL" || type === "EVP") {
      queryKey = queryKey.toLowerCase();
    }

    try {
      const url = `${baseUrl}/pix/addressKeys/external?type=${encodeURIComponent(type)}&key=${encodeURIComponent(queryKey)}`;
      const res = await this.fetchAsaasApi(url, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      const parsed = await this.parseResponseSafely(res, "GET /pix/addressKeys/external");

      if (parsed.ok && parsed.json) {
        const data = parsed.json;
        return {
          success: true,
          isValid: true,
          key: data.key || queryKey,
          type: data.type || type,
          name: data.name || data.ownerName || data.account?.name,
          taxId: data.cpfCnpj || data.ownerTaxId,
          bankName: data.bankName || data.participant?.name || data.account?.bankName,
          ispb: data.ispb || data.participant?.ispb,
          accountType: data.accountType || data.account?.accountType,
          raw: data,
        };
      }

      const errorMsg = parsed.json?.errors?.[0]?.description || parsed.json?.message || `Chave Pix não encontrada no Asaas/Banco Central (${parsed.status})`;
      return {
        success: false,
        isValid: false,
        errorMessage: errorMsg,
        raw: parsed.json,
      };
    } catch (err: any) {
      return {
        success: false,
        isValid: false,
        errorMessage: err.message || String(err),
      };
    }
  }

  // ==========================================
  // --- PROCESSAMENTO DE WEBHOOKS ---
  // ==========================================

  async processWebhook(payload: any, headers?: Record<string, string>): Promise<WebhookProcessResult> {
    const event = payload?.event || "";
    const eventId = payload?.id || payload?.eventId || "";

    if (!event) {
      return { success: false, handled: false, message: "Evento ausente no webhook" };
    }

    // Eventos de Assinatura
    if (event.startsWith("SUBSCRIPTION_")) {
      const subId = payload.subscription?.id || payload.id;
      return {
        success: true,
        handled: true,
        eventType: event,
        entityId: subId,
        status: payload.subscription?.status || "PROCESSED",
        message: `Evento de assinatura ${event} processado para ${subId}`,
      };
    }

    // Eventos de Pagamento
    if (event.startsWith("PAYMENT_")) {
      const payId = payload.payment?.id || payload.id;
      return {
        success: true,
        handled: true,
        eventType: event,
        entityId: payId,
        status: payload.payment?.status || "PROCESSED",
        message: `Evento de pagamento ${event} processado para ${payId}`,
      };
    }

    // Eventos de Transferência
    if (event.startsWith("TRANSFER_")) {
      const transferId = payload.transfer?.id || payload.id;
      return {
        success: true,
        handled: true,
        eventType: event,
        entityId: transferId,
        status: payload.transfer?.status || "PROCESSED",
        message: `Evento de transferência ${event} processado para ${transferId}`,
      };
    }

    return {
      success: true,
      handled: false,
      eventType: event,
      message: `Evento ${event} ignorado ou não aplicável.`,
    };
  }

  // ==========================================
  // --- TESTE DE CONEXÃO ---
  // ==========================================

  async testConnection(apiKey?: string, env?: "sandbox" | "production"): Promise<{ success: boolean; message: string }> {
    const targetEnv = env || (apiKey && isProductionApiKey(apiKey) ? "production" : "sandbox");

    if (apiKey) {
      if (targetEnv === "sandbox" && isProductionApiKey(apiKey)) {
        return {
          success: false,
          message: "[SAFETY FATAL ERROR]: Production API Key configured inside Sandbox environment!",
        };
      }
      if (targetEnv === "production" && isSandboxApiKey(apiKey)) {
        return {
          success: false,
          message: "[SAFETY FATAL ERROR]: Sandbox API Key configured inside Production environment!",
        };
      }
    }

    // No Node.js / Runner de Testes (ou testes que mockam fetchAsaasApi diretamente na instância)
    if (typeof window === "undefined" || (this as any).fetchAsaasApi !== AsaasGateway.prototype.fetchAsaasApi) {
      try {
        const baseUrl = PaymentEnvironmentService.getBaseUrl(targetEnv);
        const headers: Record<string, string> = { "Content-Type": "application/json" };
        if (apiKey) {
          headers["access_token"] = apiKey.trim();
        }
        const res = await this.fetchAsaasApi(`${baseUrl}/payments?limit=1`, {
          method: "GET",
          headers,
        });

        if (res.ok) {
          return {
            success: true,
            message: `Conexão com Asaas (${targetEnv.toUpperCase()}) autorizada e funcionando com sucesso!`,
          };
        }
        if (res.status === 401) {
          return {
            success: false,
            message: `HTTP 401 Unauthorized: Chave de API inválida para ambiente ${targetEnv.toUpperCase()}.`,
          };
        }
        if (res.status === 403) {
          return {
            success: false,
            message: `HTTP 403 Forbidden: Acesso negado pela API Asaas.`,
          };
        }
        return {
          success: false,
          message: `HTTP ${res.status}: Erro ao testar conexão com Asaas.`,
        };
      } catch (err: any) {
        return {
          success: false,
          message: `Erro de conexão ao testar Asaas: ${err.message || err}`,
        };
      }
    }

    // No Browser: Rota autenticada do backend para teste de conexão
    try {
      const authHeaders = await getAuthHeaders({
        "Content-Type": "application/json",
      });

      const res = await fetch("/api/admin/asaas/test-connection", {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          environment: targetEnv,
          apiKey: apiKey ? apiKey.trim() : undefined,
        }),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        return {
          success: true,
          message: data.message || `Conexão com Asaas (${targetEnv.toUpperCase()}) autorizada e funcionando com sucesso!`,
        };
      }

      return {
        success: false,
        message: data.error || `Falha na conexão com Asaas (${targetEnv.toUpperCase()}).`,
      };
    } catch (err: any) {
      return {
        success: false,
        message: `Erro de conexão ao testar Asaas: ${err.message || err}`,
      };
    }
  }
}
